import { getGridSingleSelectOperators, getGridStringOperators } from "@mui/x-data-grid-premium";
import { BRAND_DANGER, BRAND_INFO, BRAND_ORANGE, BRAND_PURPLE, BRAND_SUCCESS, BRAND_WARNING, CHART_LIGHT_GRAY } from "@/admin/color";

export class NoSuchElementError extends Error {
  constructor(message: string) {
    super(message);
  }
}

/**
 *
 * @param {string} metaName Name of the meta tag to get
 * @returns {string} Meta tag value if defined or blank
 */
export function getMetaTag(metaName: string): string {
  const metas : HTMLCollectionOf<HTMLMetaElement> = document.getElementsByTagName("meta");

  for (let i = 0; i < metas.length; i++) {
    const meta = metas[i]
    if (meta == undefined) {
      throw new Error(`metas[${i}] is undefined`);
    }

    if (meta.getAttribute("name") === metaName) {
      return meta.getAttribute("content") || "";
    }
  }

  return "";
}


/**
 * @param {string} id the element ID
 * @returns {HTMLElement} the element
 * @throws {NoSuchElementError} if element doesn't exist
 */
export function requireElementById(id: string): HTMLElement {
  const element = document.getElementById(id)
  if (!element) {
    throw new NoSuchElementError(`Element with id '${id}' not found!`)
  }
  return element
}

/**
 * @param {HTMLSelectElement} select the select element
 * @param {Array<HTMLOptionElement|[string, string]>} options the new option elements
 * @returns {void}
 */
export function replaceSelectOptions(select: HTMLSelectElement, options: Array<HTMLOptionElement | [string, string]>): void {
  while (select.length > 0) {
    select.remove(select.length - 1)
  }

  if (!options || options.length === 0) {
    return
  }

  for (const option of options) {
    if ( "tagName" in option ) {
      select.appendChild(option)
    } else {
      const optionTag = document.createElement("option")
      optionTag.innerText = option[0]
      optionTag.value = option[1]
      select.appendChild(optionTag)
    }
  }
}

/**
 * @param {string} id The overall modal ID
 * @param {string} title The new title
 */
export function setModalTitle(id: string, title: string): void {
  $("#" + id + " .modal-title").text(title)
}

/**
 * @param {string} id the id of the modal
 * @param {string|HTMLElement} html the body
 * @param {string} showOrHide "show" or "hide" - whether to show or hide the modal after replacing content
 * @param {Function} setup callback to run after content is replaced, takes two arguments: 1) modal, 2) modal body
 */
export function replaceModalHtml(
  id: string,
  html: string | HTMLElement,
  showOrHide?: "show" | "hide",
  setup?: (modal: HTMLDivElement, modalBody: HTMLDivElement) => void): void {

  const modal = document.getElementById(id) as HTMLDivElement
  if (!modal) {
    throw new Error("please create a modal with id 'edit-extract-data-engineering-item-status'")
  }

  const modalBody = modal.getElementsByClassName("modal-body")[0] as HTMLDivElement

  if (typeof html === "string") {
    modalBody.innerHTML = html
  } else {
    modalBody.innerHTML = ""
    modalBody.appendChild(html)
  }

  if (typeof setup === "function") {
    setup(modal, modalBody)
  }

  if (showOrHide === "show" || showOrHide === "hide") {
    $(modal).modal(showOrHide)
  }
}

// MUI DataGrid Operators
export const equalsOperator = getGridStringOperators().filter((op) => op.value === "equals");
export const containsOperator = getGridStringOperators().filter((op) => op.value === "contains");
export const theIsOperator = getGridSingleSelectOperators().filter((op) => op.value === "is");

const dateTimeFormat = new Intl.DateTimeFormat("en-US", {
  year: "2-digit",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  hour12: true,
  timeZone: "UTC",
});

/**
 * Formats a supplied datetime to a string that looks like <code>7/28/26, 12:00:00 AM</code>. Times are in UTC.
 * @param dateTime a date object or string that can be passed to {@link Date}'s constructor
 * @returns a date string, or an empty string if <code>dateTime</code> is null
 */
export const formatToShortUSDateTime = (
  dateTime: string | Date | null): string => {
  if (!dateTime) return "";

  const date = dateTime instanceof Date ? dateTime : new Date(dateTime);

  if (isNaN(date.getTime())) return "";

  return dateTimeFormat.format(date);
};

const dateFormat = new Intl.DateTimeFormat("en-US", {
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
  timeZone: "UTC",
});

// Date Formatter
export const formatDateToUSStyleUTC = (dateInput: string | Date | null) => {
  if (!dateInput) return "";

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return "";

  // MM/DD/YYYY output format
  return dateFormat.format(date);
};

const yearMonthFormat = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "2-digit",
  timeZone: "UTC",
});

export const formatDateToYearMonth = (dateInput: string | Date | null) => {
  if (!dateInput) return "";

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return "";

  // YYYY-MM output format
  const { year, month } = Object.fromEntries(
    yearMonthFormat.formatToParts(date).map(({ type, value }) => [type, value])
  );
  return `${year}-${month}`;
};

export const mapNtoNoandYtoYes = (yOrNstring: string): string => {
  return yOrNstring.toUpperCase() === "N" ? "No" : "Yes";
};

export const colorPicker = (str: string): string => {
  const lower = str.toLowerCase();

  // Define mappings with either literal strings or regex patterns
  const colorMappings: { color: string; words: (string | RegExp)[] }[] = [
    { color: BRAND_SUCCESS, words: ["in progress", "picked up by orchestration"] },
    { color: BRAND_INFO, words: ["complete", "product"] },
    { color: BRAND_WARNING, words: [/waiting/, "ready", "pending"] },
    { color: BRAND_DANGER, words: ["error", "failed", "cancelled"] },
    { color: BRAND_ORANGE, words: ["cancelling"] },
    { color: BRAND_PURPLE, words: ["item"] }, // purple
  ];

  // Iterate through mappings and check for a match
  for (const { color, words } of colorMappings) {
    if (
      words.some((word) =>
        typeof word === "string" ? lower === word : word.test(lower)
      )
    ) {
      return color;
    }
  }

  return CHART_LIGHT_GRAY; // default color if no match is found
};
